import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { ModalController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { distinctUntilChanged, Subscription } from "rxjs";
import * as fromApp from "../../store/app.reducer";
import * as selectors from "../../store/app.selectors";
import * as _ from "lodash";
import { resolveMobileorderPaymentLabel } from "src/app/services/payment-gateways/payment-method-label.helpers";
import { DineinTableLabelService } from "../../services/dinein-table-label/dinein-table-label.service";
import { DineinTableLabelContext } from "../../services/dinein-table-label/dinein-table-label.helpers";
import {
  getPartialPaymentPrices,
  PartialPaymentPrices,
} from "../../cart/cart-components/cart-prices/partial-payment-prices";
import { isKioskOrder } from "../kiosk-order";

export function formatOrderPrice(value: any): string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numericValue = Number(String(value).replace(",", "."));

  if (!Number.isFinite(numericValue)) {
    return String(value);
  }

  return numericValue.toFixed(2);
}

@Component({
  selector: "app-order-details",
  templateUrl: "./order-details.component.html",
  styleUrls: ["./order-details.component.scss"],
})
export class OrderDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() order: any;
  @Output() openTimeLineModalEvent = new EventEmitter();
  public groupApp: any;
  public selectedLanguage: any;
  public mo_base: any;
  public ordersArray: any;
  public ds_status_color: string;
  public storeInfo: any;
  public currentUser: any;
  public orderPaymentMethodDisplayLabel: string | null;
  public partialPaymentPrices: PartialPaymentPrices | null = null;
  public orderSubtotalDisplay: string | null = null;
  public orderDiscountAmountDisplay: string | null = null;
  public orderGrandTotalDisplay: string | null = null;
  public orderDeliveryCostDisplay: string | null = null;
  public orderCartCustomOptionsTotalPriceDisplay: string | null = null;
  public orderIsKiosk = false;

  private sub: Subscription;
  private ordersSub: Subscription;
  private authSub: Subscription;
  private storeInfoSub: Subscription;
  public dineinTableContext: DineinTableLabelContext = null;
  private dineinTableContextSub: Subscription;

  constructor(
    private modalController: ModalController,
    private store: Store<fromApp.AppState>,
    private dineinTableLabelService: DineinTableLabelService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.order) {
      this.orderIsKiosk = isKioskOrder(this.order);
      this.partialPaymentPrices = getPartialPaymentPrices(this.order);
      this.updateOrderPriceDisplays();
    }
  }

  ngOnInit() {
    this.orderIsKiosk = isKioskOrder(this.order);
    this.partialPaymentPrices = getPartialPaymentPrices(this.order);
    this.updateOrderPriceDisplays();
    this.dineinTableContextSub =
      this.dineinTableLabelService.context$.subscribe(() => {
        // The order being shown can belong to another store of the group.
        this.dineinTableContext =
          this.dineinTableLabelService.contextForStoreId(
            this.order && this.order.store_id
          );
      });
    if (this.order) {
      this.caclulateDsStatusColor();
      this.syncOrderPaymentMethodDisplayLabel();
    }

    this.authSub = this.store
      .select("auth")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (state && !_.isEqual(state.user, this.currentUser)) {
          this.currentUser = _.cloneDeep(state.user);
        }
      });

    this.sub = this.store
      .select("groupApp")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (
          state &&
          state.groupApp &&
          !_.isEqual(state.groupApp, this.groupApp)
        ) {
          this.groupApp = _.cloneDeep(state.groupApp);
          const base = _.cloneDeep(
            _.find(this.groupApp.languages, { mo_base: true })
          );
          if (!base) {
            this.mo_base = "en";
          } else {
            this.mo_base = base.code;
          }
          this.syncOrderPaymentMethodDisplayLabel();
        }
        if (
          state &&
          state.selectedLangugage &&
          !_.isEqual(state.selectedLangugage, this.selectedLanguage)
        ) {
          this.selectedLanguage = _.cloneDeep(state.selectedLangugage);
          this.syncOrderPaymentMethodDisplayLabel();
        }
      });

    this.storeInfoSub = this.store
      .select(selectors.getStoreInfo)
      .pipe(distinctUntilChanged())
      .subscribe((storeInfo) => {
        if (storeInfo && !_.isEqual(this.storeInfo, storeInfo)) {
          this.storeInfo = _.cloneDeep(storeInfo);
          this.syncOrderPaymentMethodDisplayLabel();
        }
      });

    this.ordersSub = this.store
      .select("orders")
      .pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (
          state &&
          state.ordersArray &&
          !_.isEqual(state.ordersArray, this.ordersArray)
        ) {
          this.ordersArray = _.cloneDeep(state.ordersArray);
          const orderFromState = _.find(this.ordersArray, {
            order_id: this.order.order_id,
            store_id: this.order.store_id,
          });

          if (!_.isEqual(this.order, _.omit(orderFromState, ["products"]))) {
            this.order = _.assign(
              _.cloneDeep(this.order),
              _.cloneDeep(orderFromState)
            );
            this.orderIsKiosk = isKioskOrder(this.order);
            this.partialPaymentPrices = getPartialPaymentPrices(this.order);
            this.updateOrderPriceDisplays();
            this.caclulateDsStatusColor();
            this.syncOrderPaymentMethodDisplayLabel();
          }
        }
      });
  }

  caclulateDsStatusColor() {
    if (this.order) {
      if (this.order.ds_status === "order_received") {
        this.ds_status_color = "processing";
      } else if (this.order.ds_status === "order_picked_up") {
        this.ds_status_color = "processing";
      } else if (this.order.ds_status === "order_delivered") {
        this.ds_status_color = "complete";
      } else if (this.order.ds_status === "order_rejected") {
        this.ds_status_color = "canceled";
      }
    }
  }

  sendOpenTimeLineModalEvent() {
    this.openTimeLineModalEvent.emit(Date.now().toString());
  }

  isNum(val) {
    return /^\d+$/.test(val);
  }

  private syncOrderPaymentMethodDisplayLabel() {
    const paymentMethod = this.getOrderPaymentGateway(
      this.order?.selectedPaymentMethodID
    );

    this.orderPaymentMethodDisplayLabel = resolveMobileorderPaymentLabel(
      paymentMethod,
      this.selectedLanguage,
      this.mo_base
    );
  }

  private updateOrderPriceDisplays() {
    this.orderSubtotalDisplay = formatOrderPrice(this.order?.subtotal);
    this.orderDiscountAmountDisplay = formatOrderPrice(
      this.order?.discount_amount
    );
    this.orderGrandTotalDisplay = formatOrderPrice(this.order?.grand_total);
    this.orderDeliveryCostDisplay = formatOrderPrice(this.order?.deliveryCost);
    this.orderCartCustomOptionsTotalPriceDisplay = formatOrderPrice(
      this.order?.cartCustomOptionsTotalPrice
    );
  }

  private getOrderPaymentGateway(paymentMethodID: string | null) {
    if (!paymentMethodID || !this.order) {
      return null;
    }

    if (
      (!this.order.store_id || this.storeInfo?.store_id === this.order.store_id) &&
      this.storeInfo?.paymentGateways?.[paymentMethodID]
    ) {
      return this.storeInfo.paymentGateways[paymentMethodID];
    }

    return null;
  }

  ngOnDestroy(): void {
    if (this.dineinTableContextSub) {
      this.dineinTableContextSub.unsubscribe();
    }
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.ordersSub) {
      this.ordersSub.unsubscribe();
    }

    if (this.authSub) {
      this.authSub.unsubscribe();
    }

    if (this.storeInfoSub) {
      this.storeInfoSub.unsubscribe();
    }
  }
}
